import moment from 'moment';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Clock from './Clock';
import Button from '../Button/Button';
import Stopwatch from '../Stopwatch/Stopwatch';
import { isMomentPropType } from '../../lib/customPropTypes';
import { timezoneToOffset } from '../../lib/dateUtils';

/**
 * A timepicker component. The selected prop must be updated (with ex state) to update the timepicker.
 *
 * @property {boolean} hour24 If true show 24-hour clock (military time), if not show 12-hour clock with AM/PM
 * @property {function} onSelect When something is selected, returns a moment object
 * @property {function} onConfirm If the OK button is clicked, returns a moment object with the selected time.
 * @property {object} selected Selected time (moment object). If NULL or not passed the calendar will be set to default 00:00.
 * @property {number} utcOffset Utc offset. If not passed the default is utc time 0.
 * @property {boolean} hideAnalogClock Hides the analog clock
 * @property {boolean} showSeconds Enables/shows seconds. If false shows only hour and minute.
 *
 * @extends  React.component
 * @see TimeInput, Clock
 */
class Timepicker extends Component {
  static displayName = 'Timepicker';

  static defaultProps = {
    locale: moment.locale(),
    utcOffset: moment.utc().utcOffset(),
    showConfirm: false,
  };

  static propTypes = {
    hour24: PropTypes.bool,
    onSelect: PropTypes.func,
    onClose: PropTypes.func,
    locale: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    hideAnalogClock: PropTypes.bool,
    selected: isMomentPropType,
    showConfirm: PropTypes.bool,
    showSeconds: PropTypes.bool,
    utcOffset: PropTypes.oneOfType([ // eslint-disable-line react/no-unused-prop-types
      PropTypes.string,
      PropTypes.number,
    ]),
  };

  constructor(props) {
    super(props);
    const time = this.getLocalizedTime(props);

    this.state = {
      time,
      inputHours: this.getFormatedHour(time),
      clockMode: 'hh',
    };
  }

  componentDidUpdate(prevProps) {
    const { selected, locale, utcOffset } = this.props;
    if (
      selected &&
      (!moment(selected).isSame(prevProps.selected) ||
        locale !== prevProps.locale ||
        utcOffset !== prevProps.utcOffset)
    ) {
      const time = this.getLocalizedTime(this.props);
      this.setState({
        time,
        inputHours: this.getFormatedHour(time),
      });
    }
  }

  onKeyDown = (e) => {
    if (e.keyCode === 27) {
      if (this.props.onClose) {
        this.props.onClose();
      }
    }
    return true;
  };

  onSelect = (time) => {
    this.setState({ time, inputHours: this.getFormatedHour(time) });
    if (this.props.onSelect && !this.props.showConfirm) {
      this.props.onSelect(time ? time.clone() : null);
    }
  };

  onConfirm = () => {
    this.setState({ focused: 0 });
    if (this.props.onSelect) {
      this.props.onSelect(this.state.time ? this.state.time.clone() : null);
    }
  };

  onCancel = () => {
    this.setState({ focused: 0 });
    if (this.props.onSelect) {
      this.props.onSelect(this.props.selected ? this.props.selected : null);
    }
  };

  onAm = () => {
    const time = this.state.time.clone();
    this.onSelect(time.subtract(12, 'h'));
  };

  onPm = () => {
    const time = this.state.time.clone();
    if (time.hour() === 12) {
      this.onSelect(time.hour(0));
    } else {
      this.onSelect(time.add(12, 'h'));
    }
  };

  onChangeClock = (h, m, s) => {
    const time = this.state.time.clone().hours(this.updateHours(h)).minutes(m).seconds(s);
    this.setState({ focused: this.increaseMode(), clockMode: this.increaseMode(), inputHours: this.getFormatedHour(time) });
    this.onSelect(time);
  };

  onChangeTimeInput = (value, obj) => {
    const time = this.state.time.clone()
      .hours(this.updateHours(obj.hh)).minutes(obj.mm).seconds(obj.ss);
    this.setState({ inputHours: obj.hh });
    if (!(obj.hh === 0 && !this.props.hour24)) {
      this.onSelect(time);
    }
  };

  onInputFocus = (e, mode) => {
    this.setState({ focused: mode, clockMode: mode });
  };

  onInputBlur = (e, mode) => {
    if (mode === 'hh' && this.state.inputHours === 0) {
      const time = this.state.time.clone().hours(this.updateHours(0));
      this.onSelect(time);
    }
  };

  getLocalizedTime = (props) => (props.selected ?
    props.selected.clone().locale(props.locale).utcOffset(timezoneToOffset(props.utcOffset))
    : moment.utc().utcOffset(timezoneToOffset(props.utcOffset)).startOf('day').locale(props.locale));

  getFormatedHour = (time) => {
    if (time.hours() > 12 && !this.props.hour24) {
      return time.hours() % 12;
    } else if (time.hours() === 0 && !this.props.hour24) {
      return 12;
    }
    return time.hours();
  };

  changeAmPm = () => {
    if (this.state.time.hour() >= 12) {
      this.onAm();
    } else {
      this.onPm();
    }
  };

  increaseMode = () => {
    switch (this.state.clockMode) {
      case 'hh': return 'mm';
      case 'mm': return this.props.showSeconds ? 'ss' : 'mm';
      default: return this.props.showSeconds ? 'ss' : 'mm';
    }
  };

  updateHours = (value) => {
    if (!this.props.hour24 && this.state.time.hour() < 12 && value === 12) {
      return 0;
    } else if (!this.props.hour24 && this.state.time.hour() >= 12 && !(value >= 12)) {
      return value + 12;
    }
    return value;
  };

  timeToMs = () => {
    const { time, inputHours } = this.state;
    return (+inputHours) * 60 * 60 * 1000 + (+time.minutes()) * 60 * 1000 + (+time.seconds()) * 1000;
  };

  renderAmPm = () => {
    if (!this.props.hour24) {
      return (
        <div className="ampm">
          <div onClick={this.onAm.bind(this)} className={this.state.time.hour() < 12 ? 'ampm--active' : undefined}>AM</div>
          <div onClick={this.onPm.bind(this)} className={this.state.time.hour() >= 12 ? 'ampm--active' : undefined}>PM</div>
        </div>
      );
    }
    return null;
  };

  render() {
    const time = this.state.time.clone();
    const { clockMode, focused } = this.state;
    return (
      <div
        aria-label='core-timepicker'
        className='core-timepicker'
        tabIndex='-1'
        onKeyDown={this.onKeyDown}
        ref='timepicker'
      >
        {
          <div className='timepicker-input-wrapper'>
            <Stopwatch
              value={this.timeToMs()}
              focused={focused}
              onChange={this.onChangeTimeInput}
              onFocus={this.onInputFocus}
              onBlur={this.onInputBlur}
              showSeperators
              showMilliseconds={false}
              showSeconds={this.props.showSeconds || false}
              maxHours={this.props.hour24 ? 24 : 12}
            />
            {this.renderAmPm()}
          </div>
        }

        {!this.props.hideAnalogClock ?
          <Clock
            size={220}
            hour24={this.props.hour24}
            hour={time.hours()}
            minute={time.minutes()}
            second={time.seconds()}
            displayMode={clockMode}
            onChange={this.onChangeClock}/> : null}

        {this.props.showConfirm &&
          <div className="core-timepicker--buttons">
            <Button className={this.props.hideAnalogClock ? 'timepicker-cancel tertiary' : 'timepicker-cancel secondary'} tabIndex="0" onClick={this.onCancel}>Cancel</Button>
            <Button className="timepicker-confirm primary" tabIndex="0" onClick={this.onConfirm}>Ok</Button>
          </div>}
      </div>
    );
  }

}

export default Timepicker;
